import React from 'react'
import {useTranslation} from 'react-i18next'

import {Box, CircularProgress, Grid} from '@mui/material'

import {MenopauseAppointmentItem} from '@progyny/pages/Consultations/Menopause/useMenopauseConsultationAppointments'
import CancelConsultation from '@progyny/pages/Consultations/components/CancelConsultation'
import ConsultationRow from '@progyny/pages/Consultations/components/ConsultationRow'
import NoConsultationView from '@progyny/pages/Consultations/components/NoConsultationView'

export interface AppointmentListProps {
  items: MenopauseAppointmentItem[]
  colors: any
  status: 'upcoming' | 'completed'
  loading?: boolean
  consultationSlug?: string
  onCancelBack?: () => void
}

const AppointmentList: React.FC<AppointmentListProps> = ({
  items,
  colors,
  status,
  loading,
  consultationSlug,
  onCancelBack,
}) => {
  const {t} = useTranslation()
  const [activeCancelUrl, setActiveCancelUrl] = React.useState<string | null>(
    null
  )

  const handleBack = React.useCallback(() => {
    setActiveCancelUrl(null)
    if (onCancelBack) {
      onCancelBack()
    }
  }, [onCancelBack])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          width: '100%',
        }}
        aria-busy
      >
        <CircularProgress color='primary' size={32} />
      </Box>
    )
  }

  if (status === 'upcoming' && activeCancelUrl) {
    return (
      <CancelConsultation cancelUrl={activeCancelUrl} onBack={handleBack} />
    )
  }

  if (items.length === 0) {
    return <NoConsultationView />
  }

  return (
    <Grid container rowGap={2}>
      {items.map((item, index) => (
        <Grid item xs={12} key={item.id || index}>
          <ConsultationRow
            status={status === 'upcoming' ? 'upcoming' : 'completed'}
            sessionLabel={
              item.type === 'emotional_program' && item.sessionNumber
                ? `${t('common_session')} ${item.sessionNumber}`
                : undefined
            }
            title={
              item.type === 'onboarding'
                ? t('consultations_onboarding_section_your_onboarding_call')
                : item.title
            }
            scheduledTime={item.scheduledTime}
            colors={colors}
            language={item.appointment?.bookingLanguage}
            programSlug={item.programSlug}
            sessionNumber={item.sessionNumber}
            appointment={item.appointment}
            consultationSlug={
              item.type === 'onboarding' ? consultationSlug : undefined
            }
            onCancelRequested={
              status === 'upcoming'
                ? (url) => setActiveCancelUrl(url)
                : undefined
            }
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default AppointmentList
